import React, { useCallback, useEffect, useRef, useState } from 'react'
import * as S from './styles'
import { HomepageCarouselSlide, CarouselSearchItem } from '../../../../models/homepage-carousel/homepage-carousel-slide'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import AsyncSelect from 'react-select/async'
import { HomepageCarouselHelper } from '../../../../helpers/homepage-carousel-helper'
import { debounce, cloneDeep } from 'lodash'
import { ActionButton } from '../../../atoms'
import { EventHelper } from '@monorepo/infra'

const ALLOWED_THUMBNAIL_MIMETYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
]
const MAX_THUMBNAIL_BYTES = 5 * 1024 * 1024 // 5 MB — keep in sync with backend

/** Resolve the display image URL for a carousel slide.
 *  The backend resolves itemImageUrl to `customThumbnailUrl ?? productDefault`,
 *  so we always prefer it — this lets custom uploads show on event slides too,
 *  not just creatives. The static event icon is a last-resort fallback. */
function resolveSlideImageUrl(slide: HomepageCarouselSlide): string | undefined {
    if (slide.itemImageUrl) return slide.itemImageUrl

    if (slide.itemType === 'event' && slide.itemSubType != null) {
        const label = EventHelper.getEventTypeDescription(slide.itemSubType)
        return EventHelper.getImageFile(label)
    }
    return undefined
}

interface FormValues {
    itemId: string
    itemType: string
}

const formValidationSchema = Yup.object({
    itemId: Yup.string().required('Item is required'),
    itemType: Yup.string().required('Item type is required'),
})

interface AsyncSelectOption {
    label: string
    value: string
    itemType: 'creative' | 'event'
    imageUrl?: string
    itemSubType?: number
}

const buildOptionLabel = (item: CarouselSearchItem): string => {
    const badge = item.itemType === 'event' ? '📅 Event' : '🎨 Creative'
    return `${item.title} — ${badge}`
}

const getSearchOptionsAsync = async (
    search: string
): Promise<AsyncSelectOption[]> => {
    if (!search || search.trim().length === 0) return []

    const res = await HomepageCarouselHelper.searchItemsAsync(search, '')

    if (res.kind === 'error') {
        console.error(res.errorData)
        return []
    }

    return res.data
        .filter((x) => x.title && x.id)
        .map((x) => ({
            label: buildOptionLabel(x),
            value: x.id,
            itemType: x.itemType,
            imageUrl: x.imageUrl,
            itemSubType: x.itemSubType,
        }))
}

const debouncedSearchOptions = debounce(
    (inputValue: string, callback: (opts: AsyncSelectOption[]) => void) => {
        getSearchOptionsAsync(inputValue).then(callback)
    },
    400
)

export interface HomepageCarouselRowComponentProps {
    slide: HomepageCarouselSlide
    slideNumber: number
    onSlideDeleted: (slideId: string) => void
}

const HomepageCarouselRowComponent: React.FC<HomepageCarouselRowComponentProps> =
    (props) => {
        const [isSaving, setIsSaving] = useState(false)
        const [isDeleting, setIsDeleting] = useState(false)
        const [showConfirmModal, setShowConfirmModal] = useState(false)
        const [isUploadingThumb, setIsUploadingThumb] = useState(false)
        const [thumbError, setThumbError] = useState<string | null>(null)
        const fileInputRef = useRef<HTMLInputElement>(null)

        // savedSlide = DB truth; displaySlide = optimistic preview
        const [savedSlide, setSavedSlide] = useState(cloneDeep(props.slide))

        // displaySlide = what the UI renders. Updated optimistically on option select,
        // reverted to savedSlide on Discard, synced with savedSlide after Save.
        const [displaySlide, setDisplaySlide] = useState(cloneDeep(props.slide))

        const buildSelectedOption = (slide: HomepageCarouselSlide): AsyncSelectOption => ({
            label: slide.itemTitle
                ? buildOptionLabel({
                    id: slide.itemId,
                    title: slide.itemTitle,
                    itemType: slide.itemType,
                })
                : '',
            value: slide.itemId,
            itemType: slide.itemType as 'creative' | 'event',
        })

        const [selectedOption, setSelectedOption] = useState<AsyncSelectOption>(
            buildSelectedOption(savedSlide)
        )

        // When parent re-fetches and passes a new slide prop, sync both states.
        useEffect(() => {
            const fresh = cloneDeep(props.slide)
            setSavedSlide(fresh)
            setDisplaySlide(fresh)
        }, [props.slide])

        const formik = useFormik<FormValues>({
            initialValues: {
                itemId: savedSlide.itemId,
                itemType: savedSlide.itemType,
            },
            // Save logic lives in handleSaveClick so formik.resetForm() can be called after success.
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onSubmit: async () => {},
            validationSchema: formValidationSchema,
        })

        const handleSaveClick = useCallback(async () => {
            setIsSaving(true)

            const res = await HomepageCarouselHelper.updateSlideAsync(
                savedSlide._id,
                formik.values.itemId,
                formik.values.itemType
            )

            if (res.kind === 'error') {
                console.error(res.errorData)
                // Revert display to saved state on error
                setDisplaySlide(cloneDeep(savedSlide))
                setSelectedOption(buildSelectedOption(savedSlide))
                formik.resetForm({
                    values: { itemId: savedSlide.itemId, itemType: savedSlide.itemType },
                })
                setIsSaving(false)
                return
            }

            // Save succeeded — update DB truth, sync display, clear dirty state
            const updated: HomepageCarouselSlide = { ...savedSlide, ...res.data }
            setSavedSlide(updated)
            setDisplaySlide(updated)
            setSelectedOption(buildSelectedOption(updated))
            // Reset formik to the new saved values — clears dirty flag, hides Save/Discard
            formik.resetForm({
                values: { itemId: updated.itemId, itemType: updated.itemType },
            })

            setIsSaving(false)
        }, [savedSlide, formik])

        // Discard: revert displaySlide → savedSlide (original DB state), reset formik + dropdown.
        const handleDiscardClick = useCallback(() => {
            setDisplaySlide(cloneDeep(savedSlide))
            setSelectedOption(buildSelectedOption(savedSlide))
            formik.resetForm({
                values: { itemId: savedSlide.itemId, itemType: savedSlide.itemType },
            })
        }, [savedSlide, formik])

        const handleOptionChange = useCallback(
            (option?: AsyncSelectOption) => {
                if (!option) return

                setSelectedOption(option)
                formik.setFieldValue('itemId', option.value ?? '')
                formik.setFieldValue('itemType', option.itemType ?? 'creative')

                // Optimistic preview: update displaySlide with the newly selected item.
                // IMPORTANT: also update itemType so resolveSlideImageUrl picks the
                // correct branch (events use itemSubType → static image; creatives use imageUrl).
                setDisplaySlide((prev) => ({
                    ...prev,
                    itemType:    option.itemType,
                    itemTitle:   option.label.split(' — ')[0] ?? '',
                    itemImageUrl: option.itemType === 'creative' ? option.imageUrl : undefined,
                    itemSubType:  option.itemType === 'event'    ? option.itemSubType : undefined,
                }))
            },
            [formik]
        )

        // ─── Delete handlers ──────────────────────────────────────────────────────

        const handleDeleteIconClick = useCallback(() => {
            setShowConfirmModal(true)
        }, [])

        const handleCancelDelete = useCallback(() => {
            setShowConfirmModal(false)
        }, [])

        const handleConfirmDelete = useCallback(async () => {
            setIsDeleting(true)
            const res = await HomepageCarouselHelper.deleteSlideAsync(savedSlide._id)
            setIsDeleting(false)
            if (res.kind === 'error') {
                console.error(res.errorData)
                setShowConfirmModal(false)
                return
            }
            setShowConfirmModal(false)
            props.onSlideDeleted(savedSlide._id)
        }, [savedSlide._id, props.onSlideDeleted])

        // ─── Custom thumbnail handlers ────────────────────────────────────────────

        const handleChangeThumbnailClick = useCallback(() => {
            setThumbError(null)
            fileInputRef.current?.click()
        }, [])

        const handleFileSelected = useCallback(
            async (e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0]
                // Always reset the input so the same file can be selected again after an error.
                if (fileInputRef.current) fileInputRef.current.value = ''
                if (!file) return

                // Client-side validation — mirrors backend limits for fast feedback.
                if (!ALLOWED_THUMBNAIL_MIMETYPES.includes(file.type)) {
                    setThumbError('Use JPG, PNG, WEBP, or GIF')
                    return
                }
                if (file.size > MAX_THUMBNAIL_BYTES) {
                    setThumbError('File must be under 5 MB')
                    return
                }

                setThumbError(null)
                setIsUploadingThumb(true)

                const res = await HomepageCarouselHelper.uploadSlideThumbnailAsync(
                    savedSlide._id,
                    file
                )

                setIsUploadingThumb(false)

                if (res.kind === 'error') {
                    console.error(res.errorData)
                    setThumbError('Upload failed. Please try again.')
                    return
                }

                const updated: HomepageCarouselSlide = { ...savedSlide, ...res.data }
                setSavedSlide(updated)
                setDisplaySlide((prev) => ({
                    ...prev,
                    itemImageUrl: updated.itemImageUrl,
                    customThumbnailUrl: updated.customThumbnailUrl,
                }))
            },
            [savedSlide]
        )

        const handleResetThumbnailClick = useCallback(async () => {
            setThumbError(null)
            setIsUploadingThumb(true)

            const res = await HomepageCarouselHelper.clearSlideThumbnailAsync(
                savedSlide._id
            )

            setIsUploadingThumb(false)

            if (res.kind === 'error') {
                console.error(res.errorData)
                setThumbError('Reset failed. Please try again.')
                return
            }

            const updated: HomepageCarouselSlide = { ...savedSlide, ...res.data }
            // The server clears customThumbnailUrl and returns the enriched default
            // itemImageUrl. Ensure local state drops customThumbnailUrl even if the
            // server response doesn't include the field explicitly.
            updated.customThumbnailUrl = undefined
            setSavedSlide(updated)
            setDisplaySlide((prev) => ({
                ...prev,
                itemImageUrl: updated.itemImageUrl,
                customThumbnailUrl: undefined,
            }))
        }, [savedSlide])

        return (
            <>
                <S.MainBox>
                    {/* ── Red circular minus icon on the left ── */}
                    <S.DeleteButton
                        aria-label="Delete slide"
                        disabled={isSaving || isDeleting}
                        onClick={handleDeleteIconClick}
                        title="Remove this slide"
                    >
                        &minus;
                    </S.DeleteButton>

                    <S.ContentBox>
                        <S.SlideLabelBox>
                            <S.SlideTitleBox>{displaySlide.itemTitle}</S.SlideTitleBox>
                            {displaySlide.itemType && (
                                <S.SlideTypeBadge itemType={displaySlide.itemType}>
                                    {displaySlide.itemType === 'event'
                                        ? 'Event'
                                        : 'Creative'}
                                </S.SlideTypeBadge>
                            )}
                        </S.SlideLabelBox>

                        {(() => {
                            const imgUrl = resolveSlideImageUrl(displaySlide)
                            const hasCustom = !!displaySlide.customThumbnailUrl
                            const busy = isUploadingThumb || isSaving || isDeleting
                            return (
                                <S.ThumbnailColumn>
                                    <S.ThumbnailBox>
                                        {imgUrl && (
                                            <img
                                                src={imgUrl}
                                                alt={displaySlide.itemTitle || 'Slide thumbnail'}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        )}
                                        {isUploadingThumb && (
                                            <S.ThumbnailUploadingOverlay>
                                                Uploading…
                                            </S.ThumbnailUploadingOverlay>
                                        )}
                                    </S.ThumbnailBox>

                                    <S.ThumbnailChangeButton
                                        type="button"
                                        disabled={busy}
                                        onClick={handleChangeThumbnailClick}
                                        title="Upload a custom thumbnail for this slide"
                                    >
                                        Change thumbnail
                                    </S.ThumbnailChangeButton>

                                    {hasCustom && (
                                        <S.ThumbnailResetLink
                                            type="button"
                                            disabled={busy}
                                            onClick={handleResetThumbnailClick}
                                            title="Use the item's default image"
                                        >
                                            Reset to default
                                        </S.ThumbnailResetLink>
                                    )}

                                    {thumbError && (
                                        <S.ThumbnailErrorText>{thumbError}</S.ThumbnailErrorText>
                                    )}

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={ALLOWED_THUMBNAIL_MIMETYPES.join(',')}
                                        style={{ display: 'none' }}
                                        onChange={handleFileSelected}
                                    />
                                </S.ThumbnailColumn>
                            )
                        })()}

                        <S.ItemSelectBox>
                            <S.SelectBox>
                                <AsyncSelect
                                    styles={S.ItemSelectStyleConfig}
                                    onBlur={() => formik.setFieldTouched('itemId', true)}
                                    isClearable={false}
                                    isSearchable={true}
                                    cacheOptions={false}
                                    value={selectedOption.value ? selectedOption : null}
                                    isMulti={false}
                                    loadOptions={debouncedSearchOptions}
                                    onChange={(option) =>
                                        handleOptionChange(option as AsyncSelectOption)
                                    }
                                    placeholder="Search Events or Creatives..."
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue.length === 0
                                            ? 'Start typing to search...'
                                            : 'No results found'
                                    }
                                />
                            </S.SelectBox>

                            {formik.touched.itemId && formik.errors.itemId && (
                                <S.ErrorBox>Item is required</S.ErrorBox>
                            )}

                            {formik.dirty && (
                                <S.ButtonsBox>
                                    {formik.isValid && (
                                        <S.ButtonBox>
                                            <ActionButton
                                                isDisabled={isSaving}
                                                onClick={handleSaveClick}
                                            >
                                                Save
                                            </ActionButton>
                                        </S.ButtonBox>
                                    )}
                                    <S.ButtonBox>
                                        <ActionButton
                                            isDisabled={isSaving}
                                            variant="secondary"
                                            onClick={handleDiscardClick}
                                        >
                                            Discard
                                        </ActionButton>
                                    </S.ButtonBox>
                                </S.ButtonsBox>
                            )}
                        </S.ItemSelectBox>

                        {savedSlide.lastModifiedBy && (
                            <S.LastModifiedByBox>
                                {`Last modified by ${savedSlide.lastModifiedBy}`}
                            </S.LastModifiedByBox>
                        )}
                    </S.ContentBox>
                </S.MainBox>

                {/* ── Confirm-delete modal ── */}
                {showConfirmModal && (
                    <S.ModalOverlay>
                        <S.ModalBox>
                            <S.ModalTitle>Delete Slide</S.ModalTitle>
                            <S.ModalBody>
                                Are you sure you want to remove{' '}
                                <strong>{savedSlide.itemTitle || 'this slide'}</strong>?
                                This action cannot be undone.
                            </S.ModalBody>
                            <S.ModalButtonsRow>
                                <S.ModalCancelButton
                                    onClick={handleCancelDelete}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </S.ModalCancelButton>
                                <S.ModalDeleteButton
                                    onClick={handleConfirmDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Deleting…' : 'Delete'}
                                </S.ModalDeleteButton>
                            </S.ModalButtonsRow>
                        </S.ModalBox>
                    </S.ModalOverlay>
                )}
            </>
        )
    }

export default HomepageCarouselRowComponent

